import React, { useState, useEffect, useCallback } from 'react';
import { Linkedin } from 'lucide-react';
import { teamAPI } from '../services/api';
import { team as mockTeam } from '../data/mock';

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadTeam = useCallback(async () => {
    try {
      const response = await teamAPI.getAll();
      const data = response.data;
      setTeamMembers(data.length > 0 ? data : mockTeam);
    } catch {
      setTeamMembers(mockTeam);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadTeam();
  }, [loadTeam]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-600">Loading team...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Team</h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            Meet the experts driving innovation in renewable energy consulting
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <div
                key={member._id || member.id}
                data-testid={`team-member-${member.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
              >
                <div className="relative h-80 overflow-hidden bg-gradient-to-br from-emerald-100 to-blue-100">
                  <img
                    src={member.photo}
                    alt={member.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                  <p className="text-emerald-600 font-semibold mb-4">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed mb-4">{member.bio}</p>
                  <a
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                  >
                    <Linkedin className="w-5 h-5" />
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Want to Join Our Team?</h2>
          <p className="text-xl text-gray-600 mb-8">
            We're always looking for talented professionals passionate about renewable energy
          </p>
          <a
            href="mailto:sales@solarianastra.com?subject=Career Inquiry"
            className="inline-flex items-center px-8 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default Team;
